import { SIZES } from '../firebase/schema';

const ONE_SIZE_KEY_ALIASES = new Set(['ONESIZE', 'ONE SIZE', 'ONE_SIZE', 'OS']);

function isOneSizeKey(sizeKey) {
  if (!sizeKey) return false;
  if (SIZES.ONESIZE.includes(sizeKey)) return true;
  return ONE_SIZE_KEY_ALIASES.has(String(sizeKey).toUpperCase().trim());
}

/**
 * Column order for the sticker size table (includes ONESIZE / OneSize).
 */
export function resolveStickerSizeOrder(line) {
  if (!line?.sizes || typeof line.sizes !== 'object') {
    return SIZES.ADULT;
  }

  const sizeKeys = Object.keys(line.sizes);

  if (line.sizeType) {
    const normalized = String(line.sizeType).toUpperCase().replace(/\s+/g, '_');
    if (SIZES[normalized]) {
      if (normalized === 'ONESIZE') {
        const oneSizeKeys = sizeKeys.filter(isOneSizeKey);
        return oneSizeKeys.length > 0 ? oneSizeKeys : SIZES.ONESIZE;
      }
      return SIZES[normalized];
    }
  }

  if (sizeKeys.length === 1 && isOneSizeKey(sizeKeys[0])) {
    return [sizeKeys[0]];
  }

  const sizeCounts = {
    ADULT: 0,
    ADULT_EXTENDED: 0,
    YOUTH: 0,
    TODDLER: 0,
    INFANT: 0,
    ONESIZE: 0
  };

  for (const key of sizeKeys) {
    if (isOneSizeKey(key)) {
      sizeCounts.ONESIZE += 1;
      continue;
    }
    for (const [type, list] of Object.entries(SIZES)) {
      if (type === 'ONESIZE') continue;
      if (list.includes(key)) {
        sizeCounts[type] += 1;
        break;
      }
    }
  }

  const maxCategory = Object.keys(sizeCounts).reduce((a, b) =>
    sizeCounts[a] > sizeCounts[b] ? a : b
  );

  if (sizeCounts[maxCategory] === 0) {
    return sizeKeys.length > 0 ? sizeKeys : SIZES.ADULT;
  }

  switch (maxCategory) {
    case 'YOUTH':
      return SIZES.YOUTH;
    case 'TODDLER':
      return SIZES.TODDLER;
    case 'INFANT':
      return SIZES.INFANT;
    case 'ADULT_EXTENDED':
      return [...SIZES.ADULT, ...SIZES.ADULT_EXTENDED];
    case 'ONESIZE': {
      const oneSizeKeys = sizeKeys.filter(isOneSizeKey);
      return oneSizeKeys.length > 0 ? oneSizeKeys : SIZES.ONESIZE;
    }
    default:
      return SIZES.ADULT;
  }
}

/**
 * Total piece count for the large number in the sticker footer.
 */
export function sumLinePieceCount(line, sizeOrder) {
  const fromGrid = (sizeOrder || []).reduce(
    (sum, size) => sum + (Number(line?.sizes?.[size]) || 0),
    0
  );
  if (fromGrid > 0) return fromGrid;

  const fromAllSizes = Object.values(line?.sizes || {}).reduce(
    (sum, qty) => sum + (Number(qty) || 0),
    0
  );
  if (fromAllSizes > 0) return fromAllSizes;

  return Number(line?.totalQuantity) || 0;
}

/** Fees modal → Services checkboxes (display labels). */
const FEES_MODAL_SERVICE_LABELS = [
  { key: 'shipping', label: 'Shipping' },
  { key: 'sorting', label: 'Sorting' },
  { key: 'folding', label: 'Folding' },
  { key: 'bagging', label: 'Bagging' },
  { key: 'fulfillment', label: 'Fulfillment' },
  { key: 'neckTag', label: 'Neck Tag' }
];

export function truncateStickerText(text, maxLength = 90) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  if (trimmed.length <= maxLength) return trimmed;
  if (maxLength <= 3) return '...';
  return `${trimmed.slice(0, maxLength - 3).trimEnd()}...`;
}

/**
 * Notes row: selected Services checkboxes + Other Instructions (Fees modal).
 */
export function buildStickerNotesText(order, maxLength = 90) {
  const instructions = order?.additionalInstructions || {};
  const parts = [];

  for (const { key, label } of FEES_MODAL_SERVICE_LABELS) {
    if (instructions[key]) parts.push(label);
  }

  const other = String(instructions.other || '').trim();
  if (other) parts.push(other);

  return truncateStickerText(parts.join(', '), maxLength);
}

/**
 * Date the blanks were ordered — prefer per-line timestamp from Ordering / API placement.
 */
export function resolveOrderedOnDate(line, order) {
  const candidates = [line?.orderedAt, order?.orderedAt, order?.orderedOn];
  for (const value of candidates) {
    if (value != null && value !== '') return value;
  }
  return null;
}

export function resolveTotalStickersForOrder(order) {
  if (order?.totalStickers != null && Number(order.totalStickers) > 0) {
    return Number(order.totalStickers);
  }
  if (Array.isArray(order?.lines) && order.lines.length > 0) {
    return order.lines.length;
  }
  if (order?.totalLines != null && Number(order.totalLines) > 0) {
    return Number(order.totalLines);
  }
  return 1;
}
